import {
  ImagePlus,
  Megaphone,
  MoreHorizontal,
  PlusCircle,
  RefreshCw,
  Send,
  Zap,
} from 'lucide-solid'
import {
  Component,
  createMemo,
  createSignal,
  For,
  JSX,
  Match,
  onCleanup,
  Setter,
  Show,
  Switch,
} from 'solid-js'
import { AppSchema } from '../../../../../common/types/schema'
import Button, { LabelButton } from '../../../../shared/Button'
import { DropMenu } from '../../../../shared/DropMenu'
import TextInput from '../../../../shared/TextInput'
import {
  chatStore,
  toastStore,
  userStore,
  settingStore,
  characterStore,
  ChatMessageExt,
} from '../../../../store'
import { isLoggedIn } from '/web/store/api'
import { msgStore } from '../../../../store'
import { SpeechRecognitionRecorder } from '../../../Chat/components/SpeechRecognitionRecorder'
import { Toggle } from '/web/shared/Toggle'
import { defaultCulture } from '/web/shared/CultureCodes'
import { createDebounce } from '/web/shared/util'
import { useDraft, useEffect } from '/web/shared/hooks'
import { eventStore } from '/web/store/event'
import { useAppContext } from '/web/store/context'
import NoCharacterIcon from '/web/icons/NoCharacterIcon'
import WizardIcon from '/web/icons/WizardIcon'
import { EVENTS, events } from '/web/emitter'
import { AutoComplete } from '/web/shared/AutoComplete'
import FileInput, { FileInputResult, getFileAsDataURL } from '/web/shared/FileInput'
import { embedApi } from '/web/store/embeddings'
import AvatarIcon from '/web/shared/AvatarIcon'
import TextInputAiFans from './TextInputAiFans'

const InputBarAiFans: Component<{
  chat: AppSchema.Chat
  bots: AppSchema.Character[]
  botMap: Record<string, AppSchema.Character>
  char?: AppSchema.Character
  swiped: boolean
  showOocToggle: boolean
  ooc: boolean
  setOoc: Setter<boolean | undefined>
  send: (msg: string, ooc: boolean, onSuccess?: () => void) => void
  more: (msg: string) => void
  request: (charId: string) => void
}> = (props) => {
  let ref: HTMLTextAreaElement

  const [ctx] = useAppContext()

  const user = userStore()
  const state = msgStore((s) => ({
    lastMsg: s.msgs.reduceRight<ChatMessageExt>((prev, curr, i) => {
      if (prev) return prev
      if (curr.characterId && !curr.userId) return curr
      if (i === 0) return curr
      return undefined as any
    }, undefined as any),
    msgs: s.msgs,
    canCaption: s.canImageCaption,
  }))
  const chats = chatStore((s) => ({ replyAs: s.active?.replyAs }))
  const chars = characterStore()

  useEffect(() => {
    const listener = (text: string) => {
      setText('')
      setText(text)
    }

    events.on(EVENTS.setInputText, listener)

    return () => events.removeListener(EVENTS.setInputText, listener)
  })

  const draft = useDraft(props.chat._id)

  const toggleOoc = () => {
    props.setOoc(!props.ooc)
  }

  const isOwner = createMemo(() => props.chat.userId === user.user?._id)

  const [text, setText] = createSignal(draft.text)
  const [menu, setMenu] = createSignal(false)
  const [cleared, setCleared] = createSignal(0, { equals: false })
  const [complete, setComplete] = createSignal(false)
  const [listening, setListening] = createSignal(false)

  const completeOpts = createMemo(() => {
    const list = ctx.activeBots.map((char) => ({ label: char.name, value: char._id }))
    return list
  })

  const onCompleteSelect = (opt: { label: string }) => {
    setComplete(false)
    let prev = text()
    const before = prev.slice(0, ref.selectionStart - 1)
    const after = prev.slice(ref.selectionStart)
    const next = `${before}${opt.label}${after}`
    setText(next)
    saveDraft(next)
    ref.focus()
    ref.setSelectionRange(
      before.length + opt.label.length,
      before.length + opt.label.length,
      'none'
    )
  }

  const placeholder = createMemo(() => {
    if (props.ooc) return 'Send a message... (OOC)'
    if (chats.replyAs) return `Send a message to ${ctx.allBots[chats.replyAs]?.name}...`
    return `Send a message...`
  })

  const [saveDraft, disposeSaveDraftDebounce] = createDebounce((text: string) => {
    draft.update(text)
  }, 50)

  const updateText = () => {
    if (!ref) return
    const value = ref.value || ''
    setText(ref.value || '')
    saveDraft(value)
  }

  const [send] = createDebounce(() => {
    if (!ref) return

    const value = ref.value.trim() || text().trim()
    if (!value) return

    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }

    if (props.swiped) {
      return toastStore.warn(`Confirm or cancel swiping before sending`)
    }

    props.send(value, props.ooc, () => {
      ref.value = ''
      setText('')
      setCleared(0)
      draft.clear()
    })
  }, 100)

  const createImage = () => {
    msgStore.createImage()
    setMenu(false)
  }

  const respondAgain = () => {
    msgStore.request(props.chat._id, props.chat.characterId)
  }

  const more = () => {
    props.more(state.lastMsg.msg)
    setMenu(false)
  }

  const playVoice = () => {
    const lastTextMsg = state.msgs.reduceRight<AppSchema.ChatMessage | void>((prev, curr) => {
      if (prev) return prev
      if (curr.adapter === 'image' || !curr.characterId) return
      return curr
    }, undefined)

    if (!lastTextMsg) {
      toastStore.warn(`Could not play voice: No character message found`)
      return
    }

    if (!lastTextMsg.characterId) return
    const char = ctx.allBots[lastTextMsg.characterId]
    if (!char?.voice) return

    msgStore.textToSpeech(
      lastTextMsg._id,
      lastTextMsg.msg,
      char.voice,
      props.char?.culture || defaultCulture
    )
    setMenu(false)
  }

  const triggerEvent = () => {
    const char =
      chats.replyAs && chats.replyAs in props.botMap ? props.botMap[chats.replyAs] : undefined

    eventStore.triggerEvent(props.chat, char)
    setMenu(false)
  }

  const onButtonClick = () => {
    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }
    setMenu(true)
  }

  const setAutoReplyAs = (charId: string) => {
    chatStore.setAutoReplyAs(charId)
    setMenu(false)
  }

  onCleanup(() => {
    disposeSaveDraftDebounce()
  })

  const onFile = async (files: FileInputResult[]) => {
    const [file] = files
    if (!file) return

    const buffer = await getFileAsDataURL(file.file)
    const caption = await embedApi.captionImage(buffer.content)
    setText(`*{{user}} shows {{char}} a picture that contains: ${caption}*`)
    send()
  }

  return (
    <div class="relative flex items-end gap-[10px]">
      <Show when={props.showOocToggle}>
        <div class="cursor-pointer p-2" onClick={toggleOoc}>
          <Show when={!props.ooc}>
            <WizardIcon />
          </Show>
          <Show when={props.ooc}>
            <NoCharacterIcon color="var(--bg-500)" />
          </Show>
        </div>
      </Show>

      <Show when={complete()}>
        <AutoComplete
          options={completeOpts()}
          close={() => setComplete(false)}
          onSelect={onCompleteSelect}
          dir="up"
          offset={44}
        />
      </Show>

      {/* Input field -- rounded-full, dark bg, hairline-strong border, 44px height */}
      <div
        class="flex flex-1 items-center overflow-hidden rounded-full"
        style={{
          border: '1px solid var(--cf-hairline-strong)',
          background: 'var(--cf-bg)',
          height: '44px',
        }}
      >
        <TextInputAiFans
          fieldName="chatInput"
          isMultiline
          spellcheck
          lang={props.char?.culture}
          ref={ref! as any}
          value={text()}
          placeholder={'Type a message...'}
          parentClass="flex flex-1"
          class="input-bar flex-1 rounded-none border-none bg-transparent px-[18px] font-nunito text-[14px] text-white placeholder-[rgba(255,255,255,0.35)] outline-none hover:bg-transparent focus:bg-transparent"
          onKeyDown={(ev) => {
            if (ev.key === '@') {
              setComplete(true)
            }

            const isMobileDevice = /Mobi/i.test(window.navigator.userAgent)
            const canMobileSend = isMobileDevice ? user.ui.mobileSendOnEnter : true
            if (ev.key === 'Enter' && !ev.shiftKey && canMobileSend) {
              if (complete()) return
              send()
              ev.preventDefault()
            }
          }}
          onInput={updateText}
        />
        <button
          onClick={onButtonClick}
          class="mr-2 flex items-center gap-1 rounded-full px-2.5 py-1.5 transition-colors"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid var(--cf-hairline)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
          }}
        >
          <img src="/images/generate-image-icon.png" class="h-[14px] w-[18px]" />
          <span class="font-rajdhani text-[11px] font-bold tracking-[0.5px] text-white/80">
            Ask
          </span>
          <img src="/images/chevron-icon-down.png" class="h-[7px] w-[10px]" />
        </button>
      </div>

      <DropMenu show={menu()} close={() => setMenu(false)} vert="up" horz="left">
        <div
          class="flex w-56 flex-col gap-1.5 rounded-[16px] p-2"
          style={{
            background: 'var(--cf-bg)',
            border: '1px solid var(--cf-hairline-strong)',
            'box-shadow': '0 12px 32px rgba(0,0,0,0.5)',
          }}
        >
          <Show when={ctx.activeBots.length > 1}>
            <div class="px-2 pt-1 font-rajdhani text-[10px] font-bold uppercase tracking-[1px] text-[var(--cf-muted)]">
              Auto-reply
            </div>
            <Button
              schema="secondary"
              size="sm"
              onClick={() => setAutoReplyAs('')}
              disabled={!chats.replyAs}
            >
              None
            </Button>
            <For each={ctx.activeBots}>
              {(char) => (
                <Button
                  schema="secondary"
                  size="sm"
                  onClick={() => setAutoReplyAs(char._id)}
                  disabled={chats.replyAs === char._id}
                >
                  {char.name}
                </Button>
              )}
            </For>
            <div class="my-1 h-px bg-[var(--cf-hairline)]" />
          </Show>
          <Show when={props.showOocToggle}>
            <Button
              schema="secondary"
              size="sm"
              class="flex items-center justify-between"
              onClick={toggleOoc}
            >
              <div>Stop Bot Reply</div>
              <Toggle fieldName="ooc" value={props.ooc} onChange={toggleOoc} />
            </Button>
          </Show>

          <AskMenuItem icon={<ImagePlus size={16} />} label="Generate Image" onClick={createImage} />
          <Show when={!!state.lastMsg?.characterId && isOwner()}>
            <AskMenuItem
              icon={<RefreshCw size={16} />}
              label="Respond Again"
              onClick={respondAgain}
            />
            <AskMenuItem icon={<PlusCircle size={16} />} label="Generate More" onClick={more} />
            <Show when={!!props.char?.voice?.service}>
              <AskMenuItem icon={<Megaphone size={16} />} label="Play Voice" onClick={playVoice} />
            </Show>
            <Show when={!!ctx.chat?.scenarioIds?.length && isOwner()}>
              <AskMenuItem icon={<Zap size={16} />} label="Trigger Event" onClick={triggerEvent} />
            </Show>
          </Show>
          <Show when={state.canCaption}>
            <FileInput
              fieldName="imageCaption"
              parentClass="hidden"
              onUpdate={onFile}
              accept="image/jpg,image/png,image/jpeg"
            />
            <LabelButton for="imageCaption" schema="secondary" class="w-full" alignLeft>
              Send Image
            </LabelButton>
          </Show>
        </div>
      </DropMenu>
      <Switch>
        <Match when={false && user.user?.speechtotext && (text() === '' || listening())}>
          <SpeechRecognitionRecorder
            culture={props.char?.culture}
            onText={(value) => setText(value)}
            onSubmit={() => send()}
            cleared={cleared}
            listening={setListening}
          />
        </Match>

        <Match when>
          {/* Send button -- 44px circle, grad-hot gradient */}
          <button
            onClick={send}
            class="grid flex-shrink-0 place-items-center rounded-full text-white"
            style={{
              width: '44px',
              height: '44px',
              background: 'var(--cf-grad-hot)',
              'box-shadow': '0 4px 14px rgba(138,43,226,0.4)',
              'font-size': '18px',
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
              stroke="none"
            >
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </Match>
      </Switch>
    </div>
  )
}

const AskMenuItem: Component<{
  icon: JSX.Element
  label: string
  onClick: () => void
}> = (props) => {
  return (
    <button
      onClick={props.onClick}
      class="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-left transition-colors hover:bg-[rgba(255,255,255,0.05)]"
    >
      <span
        class="grid h-8 w-8 flex-shrink-0 place-items-center rounded-[10px] text-white"
        style={{
          background: 'var(--cf-grad-hot)',
          'box-shadow': '0 4px 12px rgba(138,43,226,0.3)',
        }}
      >
        {props.icon}
      </span>
      <span class="font-rajdhani text-[13px] font-bold text-white">{props.label}</span>
    </button>
  )
}

export default InputBarAiFans
